import { useEffect, useMemo, useRef, useState } from "react";

import { PersonOutline } from "@mui/icons-material";
import { alpha, Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  getDefaultUserIndex,
  getHarnessUserLabel,
  getHarnessUsers,
  toUserData,
} from "../../utils/botpressUserProfiles.js";
import BotpressHarnessUserDialog from "../dialogs/botpressHarnessUserDialog.js";
import OmniMenuItem from "./omniMenuItem.js";
import OmniTextField from "./omniTextField.js";

const CONTROL_HEIGHT = "40px";

export const useHarnessUserSelection = (botODM, initialUserIndex = null) => {
  const harnessUsers = useMemo(() => getHarnessUsers(botODM), [botODM]);

  const [selectedUserIndex, setSelectedUserIndex] = useState(() =>
    initialUserIndex != null && initialUserIndex >= 0 && initialUserIndex < harnessUsers.length
      ? initialUserIndex
      : getDefaultUserIndex(harnessUsers),
  );

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setSelectedUserIndex(getDefaultUserIndex(harnessUsers));
  }, [harnessUsers]);

  const selectedUserData = useMemo(
    () => toUserData(harnessUsers[selectedUserIndex] ?? harnessUsers[0]),
    [harnessUsers, selectedUserIndex],
  );

  return { harnessUsers, selectedUserIndex, setSelectedUserIndex, selectedUserData };
};

const HarnessUserSelect = props => {
  const { harnessUsers, value, onChange, disabled = false, id = "selectUser", sx = {} } = props;

  const theme = useTheme();
  const [showUserInfo, setShowUserInfo] = useState(false);

  const selectedProfile = harnessUsers[value] ?? harnessUsers[0];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0,
        ...sx,
      }}
    >
      <Tooltip title="See this user's information">
        <span>
          <IconButton
            color="secondary"
            aria-label="See this user's information"
            onClick={() => setShowUserInfo(true)}
            disabled={disabled}
            sx={{
              flexShrink: 0,
              width: CONTROL_HEIGHT,
              height: CONTROL_HEIGHT,
              border: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.35)}`,
              borderRadius: theme.spacing(1),
            }}
          >
            <PersonOutline />
          </IconButton>
        </span>
      </Tooltip>

      <OmniTextField
        label="Select User"
        select
        variant="outlined"
        id={id}
        margin="none"
        value={value}
        onChange={event => onChange(Number(event.target.value))}
        placeholder={"Select User"}
        disabled={disabled}
        required
        sx={{ flex: 1, minWidth: 0 }}
      >
        {harnessUsers.map((harnessUser, index) => (
          <OmniMenuItem key={index} value={index}>
            {getHarnessUserLabel(harnessUser, index)}
          </OmniMenuItem>
        ))}
      </OmniTextField>

      {showUserInfo && (
        <BotpressHarnessUserDialog
          open={showUserInfo}
          userProfile={selectedProfile}
          userLabel={getHarnessUserLabel(selectedProfile, value)}
          onClose={() => setShowUserInfo(false)}
        />
      )}
    </Box>
  );
};

export default HarnessUserSelect;
